// The polling loop's one piece of state, kept pure — TODO T17 (30 Aug 2026).
//
// CLAUDE.md § Brain tier: "polling not sockets". What arrives is therefore a series of pages,
// each holding only what has appeared since the last cursor, and something has to turn that into
// a thread. That something is this function rather than a `useEffect`, because it is the only
// part of the loop that can be wrong in a way a screenshot does not show — a duplicated bubble, a
// cursor that stops advancing, a thread that resets to page one — and a pure function is the
// cheapest thing in the repo to test.
import type { MessagePage, MessageRow } from '@vera/bff';

export interface Thread {
  messages: MessageRow[];
  /**
   * What the next poll asks for. Carried forward when a page is empty, which is the ordinary
   * case: the BFF names the last row it returned and returns null only when it returned nothing
   * (services/bff/README.md § Polling).
   */
  cursor: string | null;
  /** True while the caller is still catching up — the page came back full. */
  hasMore: boolean;
}

export const EMPTY_THREAD: Thread = { messages: [], cursor: null, hasMore: false };

/** Two copies of one row, compared whole: the rows are plain JSON, straight off the wire. */
const sameRow = (a: MessageRow, b: MessageRow): boolean => JSON.stringify(a) === JSON.stringify(b);

export function mergeThread(previous: Thread | undefined, page: MessagePage): Thread {
  const base = previous ?? EMPTY_THREAD;

  // T107b (23 Sep 2026): a row the thread already holds can change after it was first polled —
  // a voice note's transcript, a file's reading and download, a delivery status all land after
  // the row does — so the service re-sends the last half hour of the thread (`settling`), and a
  // newer copy replaces the old one IN PLACE, keeping the thread's order. Until then a thread
  // open when a voice note arrived showed "(no text)" under it until a reload. `?? []` because a
  // bundle can briefly run against a service from before the field existed.
  const fresh = new Map(
    [...(page.settling ?? []), ...page.messages].map((message) => [message.id, message]),
  );
  let changed = false;
  const kept = base.messages.map((message) => {
    const newer = fresh.get(message.id);
    if (!newer || sameRow(message, newer)) return message;
    changed = true;
    return newer;
  });

  // Dedupe by id rather than trusting the cursor. The cursor is keyset and correct, but a retry,
  // a React strict-mode double render or a poll that overlaps a refetch can deliver the same page
  // twice, and a duplicated message in a recruiter's thread reads as a bug in kadmon, not in this.
  // A re-sent row the thread never had is dropped rather than inserted out of order.
  const known = new Set(base.messages.map((message) => message.id));
  const arrived = page.messages.filter((message) => !known.has(message.id));

  return {
    // The same array when nothing changed, so a quiet poll re-renders nothing.
    messages: !changed && arrived.length === 0 ? base.messages : [...kept, ...arrived],
    cursor: page.nextCursor ?? base.cursor,
    hasMore: page.hasMore,
  };
}
